import type {
  InsertCustomDataSourceMutation,
  Scalars,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

export interface InsertCustomDatasourceTRPCInput {
  Title: string;
  Datasources: Scalars['jsonb']['input'];
  Fields: Scalars['jsonb']['input'];
  Filters: Scalars['jsonb']['input'];
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation shape so consumers
 * don't branch on which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertCustomDataSourceMutation => {
  return {
    insert_custom_datasource_one: {
      __typename: 'custom_datasource',
      Id: trpcData.Id,
    },
  };
};

export const useInsertCustomDatasourceTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customDatasource.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasources.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasourceById.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertCustomDatasource: async (
      variables: InsertCustomDatasourceTRPCInput
    ): Promise<InsertCustomDataSourceMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
